import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Form, Button, InputGroup } from "react-bootstrap";
import { Formik, FormikProps } from "formik";
import { ActionHandlerFunction } from "@preveil-api";
import { Account, FormValidationLimits, AccountMessages, AccountErrorMessages, usePasswordRemoteValidation, useCreateNewPassword, LoginWebUIActionTypes } from "src/common";
import { Loading, Icon } from "src/components";
import * as yup from "yup";

type AllProps = {
  current_account: Account;
  auth_token: string;
  handleAction: ActionHandlerFunction;
}

type FormProps = {
  new_password: string;
  confirm_password: string;
}

/* Description: Change password form shown during login when password validation fails */
function ChangePasswordComponent(props: AllProps) {
  const { current_account, auth_token, handleAction } = props;
  const [show_password, setShowPassword] = useState<boolean>(false);
  const [show_confirm_password, setShowConfirmPassword] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const formik_ref = useRef<FormikProps<FormProps>>(null);
  const password_ref = useRef<HTMLInputElement>(null);
  const { validated, error: invalid_password, validatePassword, resetValidation } = usePasswordRemoteValidation(Account.getUserIdentifiers(current_account));
  const { new_auth_token, createNewPassword } = useCreateNewPassword(current_account, auth_token);
  const schema = yup.object().shape({
    new_password: yup.string()
      .required(AccountErrorMessages.password_required_error)
      .min(
        FormValidationLimits.PASSWORD_MIN_LENGTH,
        AccountErrorMessages.password_helper
      ),
    confirm_password: yup.string()
      .required(AccountErrorMessages.password_required_error)
      .oneOf([yup.ref("new_password"), null], AccountErrorMessages.password_match_error)
  });

  // Description: Handles the trigger of success or errors from validation completion from hook
  useEffect(() => {
    !!validated && createNewPassword(validated);
    !!invalid_password && handleInvalidPassword(invalid_password);
  }, [validated, invalid_password]);

  // Description: Handles the trigger of success from validation completion from hook
  useEffect(() => {
    !!new_auth_token && handleSuccess(new_auth_token);
  }, [new_auth_token]);

  // Description: Handle form submit
  function handleSubmit(form_values: FormProps) {
    const params = form_values.new_password;

    // NOTE: Clear error message from previous attempts
    formik_ref.current?.setStatus(null);
    setValidating(true);
    validatePassword(params);
  }

  // Description: Handle success after remote validation (params is the fully validated password)
  function handleSuccess(new_auth_token: string) {
    clearFormState();
    handleAction({ actionType: LoginWebUIActionTypes.SubmitPasswordChange, new_auth_token });
  }

  // Description: Handle invalid password after remote validation
  function handleInvalidPassword(message: string) {
    formik_ref.current?.resetForm();
    formik_ref.current?.setStatus({
      error: message
    });
    password_ref.current?.focus();
    clearFormState();
  }

  // Description: Handle Form reset and clear loading styles
  function handleReset() {
    clearFormState();
    formik_ref.current?.resetForm();
  }

  // Description: Only clears the form but stays in password step
  function clearFormState() {
    resetValidation();
    setValidating(false);
  }

  return <div className={validating ? "validating" : ""}>
    {
      validating &&
      <Loading message={AccountMessages.validating_password} className="mt-2" />
    }
    <div className="mb-4">
      <h3 className="border-bottom pb-3">Update Your Password</h3>
      <p><Icon className="ficon-alert-triangle pv-orange me-2" />
        Your current password doesn't meet our security standards.
        Please create a new, stronger password to continue.
      </p>
    </div>
    <Formik
      innerRef={formik_ref}
      validationSchema={schema}
      onSubmit={handleSubmit}
      initialValues={{
        new_password: "",
        confirm_password: ""
      }}>
      {({ dirty, values, errors, touched, handleChange, handleSubmit, handleBlur, isValid, status, setStatus }) => {
        // NOTE: Wrapper to clear status on any change
        const handleFieldChange = (e: ChangeEvent<any>) => {
          if (status?.error) {
            setStatus(null);
          }
          handleChange(e);
        };

        return (
          <Form noValidate onSubmit={handleSubmit} className="max-600">
            <Form.Group className="mb-3 form-group">
              <Form.Label htmlFor="new_password_tb" className="fs-6">New Password</Form.Label>
              <InputGroup className={`password-form-control ${!!errors.new_password && touched.new_password ? "invalid" : ""}`} hasValidation={true}>
                <Icon className="ficon-lock" />
                <Form.Control
                  ref={password_ref}
                  id="new_password_tb"
                  type={!!show_password ? "text" : "password"}
                  autoFocus={true}
                  placeholder="Enter New Password"
                  name="new_password"
                  value={values.new_password}
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                  isInvalid={!!errors.new_password && touched.new_password}
                />
                <Button
                  variant="transparent"
                  className="btn-icon"
                  size="sm"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!show_password)}
                  aria-label="show password"
                >
                  <span>
                    <Icon className={!!show_password ? "ficon-eye" : "ficon-eye-off"} />
                  </span>
                </Button>
              </InputGroup>
              {!!status?.error ? (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {status.error}
                </Form.Control.Feedback>
              ) : !!errors.new_password && touched.new_password ? (
                <Form.Control.Feedback type="invalid" className={`${!!errors.new_password && touched.new_password ? "d-block" : ""}`}>
                  {errors.new_password}
                </Form.Control.Feedback>
              ) : (
                !status && (
                  <Form.Text className="text-muted d-block" as="div">
                    {AccountErrorMessages.password_helper}
                  </Form.Text>
                )
              )}
            </Form.Group>

            <Form.Group className="form-group">
              <Form.Label htmlFor="confirm_password_tb" className="fs-6">Confirm New Password</Form.Label>
              <InputGroup className={`password-form-control ${!!errors.confirm_password && touched.confirm_password ? "invalid" : ""}`} hasValidation={true}>
                <Icon className="ficon-lock" />
                <Form.Control
                  id="confirm_password_tb"
                  type={!!show_confirm_password ? "text" : "password"}
                  placeholder="Confirm New Password"
                  name="confirm_password"
                  value={values.confirm_password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={!!errors.confirm_password && touched.confirm_password}
                />
                <Button
                  variant="transparent"
                  className="btn-icon"
                  size="sm"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword(!show_confirm_password)}
                  aria-label="show confirm password" >
                  <span>
                    <Icon className={!!show_confirm_password ? "ficon-eye" : "ficon-eye-off"} />
                  </span>
                </Button>
              </InputGroup>
              <Form.Control.Feedback type="invalid" className={`${!!errors.confirm_password && touched.confirm_password ? "d-block" : ""}`}>
                {errors.confirm_password}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="btn-panel mt-4">
              <Button type="submit" disabled={!dirty || !isValid}>
                Update Password
              </Button>
              <Button variant="no-outline-secondary" type="button" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  </div>;
}

export default React.memo(ChangePasswordComponent);